import { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';

/**
 * Confirm dialog - custom replacement for native confirm().
 * Uses the three-state animation pattern.
 */
export const ConfirmDialog = observer(function ConfirmDialog() {
  const [shouldRender, setShouldRender] = useState(false);
  const [animationState, setAnimationState] = useState<'entering' | 'exiting' | 'visible'>(
    'exiting',
  );

  const dialog = bibleState.confirmDialog;
  const isVisible = dialog !== null;

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      setAnimationState('entering');
      const timer = setTimeout(() => setAnimationState('visible'), 250);
      return () => clearTimeout(timer);
    } else {
      setAnimationState('exiting');
      const timer = setTimeout(() => setShouldRender(false), 200);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  const handleConfirm = useCallback(() => {
    dialog?.onConfirm();
    bibleState.hideConfirmDialog();
  }, [dialog]);

  const handleCancel = useCallback(() => {
    dialog?.onCancel?.();
    bibleState.hideConfirmDialog();
  }, [dialog]);

  // Handle escape key
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCancel();
      } else if (e.key === 'Enter') {
        handleConfirm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isVisible, handleConfirm, handleCancel]);

  if (!shouldRender || !dialog) return null;

  return (
    <>
      {/* Overlay */}
      <div className={`confirm-dialog-overlay ${animationState}`} onClick={handleCancel} />

      {/* Dialog */}
      <div
        className={`confirm-dialog ${animationState === 'entering' ? 'entering' : ''} ${animationState === 'exiting' ? 'closing' : ''}`}
      >
        <h3 className="mb-2 text-lg font-semibold text-stone-800 dark:text-stone-200">
          {dialog.title || 'Confirm'}
        </h3>

        <p className="mb-4 text-stone-600 dark:text-stone-400">{dialog.message}</p>

        <div className="flex gap-3">
          <button
            onClick={handleCancel}
            className="flex-1 rounded-xl bg-stone-200/50 py-2.5 text-base font-medium text-stone-600 transition-all hover:bg-stone-300/70 dark:bg-stone-700/30 dark:text-stone-400 dark:hover:bg-stone-600/40"
          >
            {dialog.cancelText || 'Cancel'}
          </button>
          <button
            onClick={handleConfirm}
            className={`flex-1 rounded-xl py-2.5 text-base font-medium transition-all ${
              dialog.variant === 'danger'
                ? 'bg-red-500 text-white hover:bg-red-600'
                : 'bg-cyan-500 text-white hover:bg-cyan-600'
            }`}
          >
            {dialog.confirmText || 'Confirm'}
          </button>
        </div>
      </div>
    </>
  );
});

export default ConfirmDialog;
